/**
 * Billing in the app chrome: the one billing banner full width under the titlebar (pushing content down), the plan
 * picker sheet (opened from that banner, the composer notice, Settings → Billing), and the toast for a write refused
 * with `billing_required` / `plan_limit_members`, and the storage-full prompt for an upload refused with
 * `plan_limit_storage` (BillingViewModel `noteFault`). Renders nothing without BillingContext.
 */
import { useT } from "../../i18n/I18nProvider.tsx";
import { useBilling } from "../../context/BillingContext.tsx";
import { Toast } from "../../primitives/index.ts";
import { BillingBannerView, StorageBannerView } from "./BillingBanners.tsx";
import { PlanPickerSheet } from "./PlanPickerSheet.tsx";
import { StorageFullDialog } from "./StorageFullDialog.tsx";

export type BillingChromeProps = {
  workspaceName: string;
  /** Hide the banner (on Settings → Billing, which shows its own). */
  hideBanner?: boolean;
  onContact: () => void;
  /** A write refused for billing (fault code), shown once as a toast. */
  fault?: "billing_required" | "plan_limit_members";
  onFaultClose: () => void;
};

/** The banner slot (place it under the titlebar). */
export function BillingChromeBanner({ workspaceName, hideBanner, onContact }: Pick<BillingChromeProps, "workspaceName" | "hideBanner"> & { onContact?: () => void }) {
  const billing = useBilling();
  if (billing === null || hideBanner === true) return null;
  const banner = billing.state.banner;
  if (banner === null) {
    const storage = billing.state.storageBanner;
    if (storage === null) return null;
    return <StorageBannerView vm={billing.vm} banner={storage} workspaceName={workspaceName} {...(onContact === undefined ? {} : { onContact })} testID="storage-banner" />;
  }
  return <BillingBannerView vm={billing.vm} state={billing.state} banner={banner} workspaceName={workspaceName} placement="edge" testID="billing-banner" />;
}

/** The picker sheet and the fault toast (mount once in the shell). */
export function BillingChromeHost({ workspaceName, onContact, fault, onFaultClose }: Omit<BillingChromeProps, "hideBanner">) {
  const t = useT();
  const billing = useBilling();
  if (billing === null) return null;
  const message = fault === "billing_required" ? t("billing.fault.required", { ws: workspaceName }) : fault === "plan_limit_members" ? t("billing.fault.members", { ws: workspaceName }) : "";
  const manage = billing.state.view?.manage === true;
  return (
    <>
      {manage ? <PlanPickerSheet vm={billing.vm} state={billing.state} onContact={onContact} /> : null}
      <StorageFullDialog vm={billing.vm} state={billing.state} workspaceName={workspaceName} onContact={onContact} testID="storage-full" />
      <Toast
        open={fault !== undefined}
        onClose={onFaultClose}
        message={message}
        closeLabel={t("common.close")}
        durationMs={6000}
        {...(manage && fault === "billing_required"
          ? {
              action: {
                label: t("billing.subscribe"),
                onPress: () => {
                  onFaultClose();
                  billing.vm.openPicker();
                },
                testID: "billing-fault-subscribe",
              },
            }
          : {})}
        testID="billing-fault"
      />
    </>
  );
}
